import { useCallback, useEffect, useRef, useState } from 'react'

import ErrorProvider from '../errorHandler/ErrorProvider'
import { setStorage } from '../storage/setStorage'



const useLocalStorage = <T>(key: string, defaultValue: T) => {
  const cachedKey = useRef(key)

  const localValue = localStorage.getItem(key)

  const [value, setValue] = useState<T>(localValue ? JSON.parse(localValue) : defaultValue)

  /**
   * Update the entire stored value
   */
  const updateLocalStorage = useCallback(
    (newValue: T) => {
      setStorage(localStorage, key, JSON.stringify(newValue)).catch((err) => {
        ErrorProvider.showError(err)
      })

      setValue(newValue)
    },
    [key],
  )

  /**
   * Update only part(s) of the stored value (merging with the existing value)
   */
  const updatePartialLocaleStorage = useCallback(
    (newValue: Partial<T>) => {
      const newObject = { ...value, ...newValue }

      setStorage(localStorage, key, JSON.stringify(newObject)).catch((err) => {
        ErrorProvider.showError(err)
      })

      setValue(newObject)
    },
    [key, value]
  )

  useEffect(() => {
    if (cachedKey.current !== key) {
      cachedKey.current = key

      const newValue = localStorage.getItem(key)

      setValue(newValue ? JSON.parse(newValue) : defaultValue)
    }
  }, [defaultValue, key])

  return [value, updateLocalStorage, updatePartialLocaleStorage] as const
}

export default useLocalStorage
